import {
  type DraftDefinitie,
  CriterionOutcome,
  type Definitie,
  type Gap,
  Event,
  type BaselineState,
  Instantie,
  type Verdict,
  type Projection,
  type Permission,
  type Role,
  Toekenning,
  harden,
} from '@eformulieren/model'
import { FIRST_BEURT, beurtAfter } from './beurt.js'
import { outstandingCriteria } from './criteria.js'
import { computeProjectionFromToekenningen } from './projection.js'
import type { State } from './state.js'
import { determineDifference } from './difference.js'
import {
  type Comment,
  type Werkverzameling,
  determineWerkverzameling,
  commentsFrom,
  sectionsToCompare,
  werkverzamelingRule,
} from './werkverzameling.js'

/**
 * Het voorbeeld: het formulier zoals de invuller het krijgt, terwijl de inrichter het maakt
 * (D-22, ontwerp 8.7).
 *
 * **Er komt geen tweede projectieberekening.** `computeProjectionFromToekenningen` is de enige, en
 * dit bestand levert haar de ingrediënten die designtime niet bestaan: er is geen instantie, geen
 * toekenning en geen token. Het voorbeeld zet die op, en wat er dan uitkomt is per constructie wat
 * de pod zou aanleveren (ontwerp 11.1 punt 1). Zou dit bestand de rechten per sectie zelf
 * uitrekenen, dan is het voorbeeld een tweede lezing van U6, R-65a tot R-65c en R-193, en dan
 * toont het iets wat de invuller nooit krijgt. Precies dat stond tot nu toe twee keer los in de
 * repo: in `packages/renderer/harness/main.tsx` en in `packages/renderer/test/projectie.ts`.
 *
 * **De vier assen van 8.7.** Rol en deling zitten in {@link ExampleArrangement}, de overdracht
 * in {@link ExampleMoment}, en de weergave niet: die is een prop van de renderer en geen
 * ingrediënt van de projectie (R-138a, B7).
 *
 * **Wie er kijkt volgt uit de deling en niet uit een rol.** Zonder deling kijkt wie de laag van
 * de rol bezit, met een toekenning van het processysteem: de aanvrager als invuller (R-69), de
 * toetser als toetser (R-172b). Met een deling kijkt een genodigde mee, met een toekenning uit
 * een deling van die eerste, en die bezit de laag juist niet: een medeschrijver van de invuller
 * (R-64), of een adviseur met wie de toetser deelt (R-184). Dat onderscheid is de kern van U6, en
 * het is de reden dat de deling een eigen as is en geen derde rol.
 */

/** De zeven momenten in het heen en weer: het begin, en na elk van de zes gebeurtenissen (D-61). */
export type ExampleMoment = 'begin' | Event

/** Alle momenten, in de orde waarin ze in een beurtenreeks langskomen. */
export const EXAMPLE_MOMENTS: readonly ExampleMoment[] = ['begin', ...Event.options]

/** Waarlangs het voorbeeld te bekijken is, op de weergave na (ontwerp 8.7). */
export interface ExampleArrangement {
  /** De rol waaruit wordt gekeken, en daarmee de laag (D-23, R-172b). */
  readonly rol: Role
  /**
   * Het recht per sectie-id uit een deling, of niets voor de eigenaar van de laag zelf (D-23, U6).
   *
   * Een leeg record is hetzelfde als geen enkel recht: er is dan een genodigde die niets mag
   * zien. Het levert een lege projectie op, en dat is een uitkomst en geen fout (R-68).
   */
  readonly deling?: Readonly<Record<string, Permission>>
  /** Op welk moment in het heen en weer (D-61). */
  readonly moment: ExampleMoment
  /** De antwoorden die in het voorbeeld zijn ingevuld; nergens opgeslagen (D-24). */
  readonly stand: State
}

/**
 * De verzonnen instantie waarop het voorbeeld draait.
 *
 * Vaste waarden en geen gegenereerde: het voorbeeld is geen publicatie en geen uitvraag, en een
 * id dat per render verschilt maakt de uitkomst niet-deterministisch (R-01e). De id's komen uit
 * `nieuwId` en zijn daarna vastgelegd, want met de hand verzonnen identifiers voldoen zelden aan
 * de eigen regels (ontwerp 11.4). De personen zijn verzonnen en de adressen staan op
 * voorbeeld.nl, zoals de repo-afspraak eist.
 */
const EXAMPLE = {
  instantieId: 'ins-7k2m4npq',
  publicatieId: 'pub-3x8v2hqa',
  /** De toekenning van het processysteem aan wie de laag van de rol bezit (R-155b). */
  toekenningId: 'tkn-myzpk887r6',
  /** De toekenning uit de deling aan de genodigde (R-155b). */
  delingId: 'tkn-ydqrz0ptr0',
  dossierId: 'voorbeeld-dossier',
  uitvraagRef: 'voorbeeld-uitvraag',
  /** Wie de laag van de rol bezit, met een toekenning van het processysteem (R-69, R-172b). */
  eigenaar: 'voorbeeld:eigenaar',
  /** Wie via een deling meekijkt: een medeschrijver of een adviseur (R-64, R-184). */
  genodigde: 'voorbeeld:genodigde',
  /** Eén vast moment; een deling in een voorbeeld heeft geen geschiedenis. */
  moment: '2026-01-01T00:00:00Z',
  /** Er bestaat in het voorbeeld geen revisie; dit is net zo fictief als `instantieId`. */
  revisieId: 'rev-8h3k2mzq',
  /** Een vaste, in de toekomst liggende termijn, voor de contextkop van {@link exampleContext}. */
  deadline: '2026-03-15T17:00:00Z',
} as const

/**
 * De projectie van dit voorbeeld: wat deze kijker op dit moment van dit formulier krijgt.
 *
 * De definitie moet verhard zijn, want dat is wat de invuller krijgt (R-99). Wie een
 * conceptstand in beeld wil hebben laat {@link exampleDefinitie} er eerst langs.
 */
export function exampleProjection(definitie: Definitie, arrangement: ExampleArrangement): Projection {
  const beurt = arrangement.moment === 'begin' ? FIRST_BEURT : beurtAfter(arrangement.moment)
  /**
   * Wie er kijkt hangt aan het bestaan van de as en niet aan de inhoud ervan.
   *
   * Een genodigde met nul rechten is een genodigde: hij bezit de laag niet en ziet niets. Zou
   * dat hier op het aantal rechten hangen, dan viel een leeg record terug op de eigenaar en zag
   * de inrichter juist alles.
   */
  const invited = arrangement.deling !== undefined
  const permissions = arrangement.deling ?? {}
  /** Een deling zonder enkel recht bestaat niet (R-65), dus dan gaat er geen deling mee. */
  const shared = Object.keys(permissions).length > 0

  const instantie = Instantie.parse({
    id: EXAMPLE.instantieId,
    publicatieId: EXAMPLE.publicatieId,
    dossierId: EXAMPLE.dossierId,
    uitvraagRef: EXAMPLE.uitvraagRef,
    eigenaar: EXAMPLE.eigenaar,
    status: beurt.status,
    actieveLaag: beurt.laag,
    voorganger: null,
    voorgangerRevisie: null,
    ijkstand: null,
    // Designtime is er geen dossier, dus een verwijzing heeft geen inhoud om te tonen (R-54).
    dossierstanden: {},
    dossierdocumenten: {},
  })

  const side = sideOf(arrangement.rol)

  /**
   * De toekenning van het processysteem aan wie de laag van deze rol bezit (R-155b, R-172b).
   *
   * Zonder deling kijkt hij zelf; met een deling is hij de deler, en dan is zijn toekenning wat
   * hij op dit moment zelf heeft. Dat is niet decoratie: een deling geeft nooit meer dan de deler
   * zelf heeft (R-65a), en wie in de toetserrol deelt geeft nergens bewerkrecht weg (R-65c). Zou
   * hier een vaste rol staan, dan toont het voorbeeld een adviseur die mag schrijven.
   */
  const fromOwner = Toekenning.parse({
    ...EMPTY_TOEKENNING,
    id: EXAMPLE.toekenningId,
    persoon: EXAMPLE.eigenaar,
    blokken: [definitie.blok],
    rol: arrangement.rol,
    kant: side,
    bron: 'processysteem',
  })

  /**
   * De deling zoals de deler haar zou maken: binnengekomen, op het blok van dit formulier, van de
   * kant en de rol waaruit dit voorbeeld kijkt. Een genodigde staat aan dezelfde kant als zijn
   * deler, want het voorbeeld kent geen tweede ingang.
   */
  const fromDeling = shared
    ? Toekenning.parse({
        ...EMPTY_TOEKENNING,
        id: EXAMPLE.delingId,
        persoon: EXAMPLE.genodigde,
        blokken: [definitie.blok],
        rol: 'invuller',
        kant: side,
        bron: 'deling',
        deler: EXAMPLE.eigenaar,
        rechten: permissions,
        instantieId: EXAMPLE.instantieId,
        toestand: 'binnengekomen',
      })
    : undefined

  return computeProjectionFromToekenningen({
    definitie,
    publicatieId: EXAMPLE.publicatieId,
    instantie,
    persoon: invited ? EXAMPLE.genodigde : EXAMPLE.eigenaar,
    kant: side,
    toegang: invited
      ? {
          eigen: fromDeling === undefined ? [] : [fromDeling],
          delers: { [EXAMPLE.eigenaar]: [fromOwner] },
        }
      : { eigen: [fromOwner], delers: {} },
    stand: arrangement.stand,
  })
}

/**
 * De velden die een toekenning met de hand zou missen: alles wat bij geen bron of bij een andere
 * bron hoort staat op `null`, en het moment is het vaste moment van dit voorbeeld. Wat per
 * toekenning verschilt zet {@link exampleProjection} erover, en `Toekenning.parse` weegt het
 * geheel, met dezelfde regels als het register (ontwerp 11.4).
 */
const EMPTY_TOEKENNING = {
  dossierId: EXAMPLE.dossierId,
  namens: null,
  deler: null,
  rechten: null,
  instantieId: null,
  toestand: null,
  einde: null,
  redencategorie: null,
  toelichting: null,
  meldingsnummer: null,
  aangemaaktOp: EXAMPLE.moment,
  vervallenOp: null,
  ingetrokkenOp: null,
} as const

/**
 * Wie er in dit voorbeeld schrijft, als tokensubject (R-154).
 *
 * De projectie draagt de persoon niet, en de renderer heeft hem nodig voor de attributie in
 * rich text (R-188) en voor de opmerkingauteur (R-73). Die komt dus hiervandaan en niet uit een
 * verzonnen waarde in het scherm: dan zou de attributie in het voorbeeld iemand noemen die in
 * de projectie niet bestaat.
 */
export function exampleIdentity(arrangement: Pick<ExampleArrangement, 'deling'>): string {
  return arrangement.deling === undefined ? EXAMPLE.eigenaar : EXAMPLE.genodigde
}

/**
 * De contextkop van dit voorbeeld: dossier, uitvraagmoment en deadline (R-125, R-128).
 *
 * **Dezelfde spiegeling als `exampleIdentity`, en om dezelfde reden.** De renderer toont
 * geen contextkop op grond van een verzonnen prop in het scherm van de configurator; hij toont
 * hem op grond van precies deze drie waarden, en die staan al vast in `EXAMPLE` en al op de
 * fictieve instantie die {@link exampleProjection} zelf opbouwt. Zonder deze functie kreeg de
 * inrichter een voorbeeld te zien dat de contextkop stilzwijgend wegliet, terwijl de instantie
 * eronder de gegevens allang droeg; de configurator zou dan iets anders tonen dan de pod, en
 * dat is precies de spiegeling die D-22 vraagt (ontwerp 8.7).
 *
 * `deadline` staat niet op de fictieve `Instantie` in {@link exampleProjection}: die berekent
 * er niets mee, en de echte pod haalt haar apart op (`GET /voortgang`, los van de projectie).
 * Hier komt hij om dezelfde reden apart vandaan.
 */
export function exampleContext(): { dossierId: string; uitvraagRef: string; deadline: string } {
  return {
    dossierId: EXAMPLE.dossierId,
    uitvraagRef: EXAMPLE.uitvraagRef,
    deadline: EXAMPLE.deadline,
  }
}

/**
 * Een gegeven oordeel, verpakt als criteriumuitkomst (D-22, D-24).
 *
 * Zelfde motief als `exampleIdentity`: het voorbeeld heeft geen echte instantie, revisie of
 * opslagroute om een oordeel naar te sturen, maar D-24 vraagt dat de regels wel werkend te
 * proberen zijn. `toetser` komt uit `exampleIdentity`, want dat is al "wie er in dit
 * voorbeeld schrijft"; `instantieId` en `revisieId` zijn even fictief als de rest van
 * `EXAMPLE`. Geparsed en niet alleen getypeerd, dezelfde regel als bij `Instantie.parse` en
 * `Toekenning.parse` hierboven: met de hand verzonnen identifiers voldoen zelden aan de eigen regels
 * (ontwerp 11.4).
 */
export function exampleVerdict(
  criterionId: string,
  issued: Pick<Verdict, 'oordeel' | 'voldoet' | 'onderbouwing'>,
  arrangement: Pick<ExampleArrangement, 'deling'>,
): CriterionOutcome {
  return CriterionOutcome.parse({
    instantieId: EXAMPLE.instantieId,
    criteriumId: criterionId,
    oordeel: {
      ...issued,
      toetser: exampleIdentity(arrangement),
      revisieId: EXAMPLE.revisieId,
      moment: EXAMPLE.moment,
    },
  })
}

/**
 * De invoer voor {@link exampleWerkverzameling}: dezelfde vier ingrediënten als
 * `getWerkverzameling` in de pod, met de ijkstand als het enige dat het voorbeeld zelf
 * moet aanleveren (ontwerp 8.7, R-75, R-176).
 */
export interface ExampleWerkverzamelingInput {
  readonly definitie: Definitie
  readonly projectie: Projection
  /** Op welk moment in het heen en weer (D-61); bepaalt de regel (D-59) net als bij de pod. */
  readonly moment: ExampleMoment
  /**
   * De ijkstand die het voorbeeld zelf heeft vastgelegd, of niets zolang er nog geen
   * toetsbeurt is geweest in deze sessie.
   *
   * **Geen verzonnen inhoud.** Anders dan de pod, die de ijkstand uit een echte revisie
   * haalt, heeft het voorbeeld die revisie niet. Wat hier binnenkomt is dus altijd wat de
   * inrichter zelf, als toetser, in dezelfde sessie heeft getypt en vastgelegd (zie
   * `Voorbeeld.tsx`) — nooit een verzonnen opmerking of een verzonnen vorig antwoord.
   */
  readonly ijkstand?: BaselineState
  /** De levende stand van de invoerlaag, om tegen de ijkstand af te zetten (R-178a). */
  readonly stand: State
}

export interface ExampleWerkverzameling {
  readonly werkverzameling: Werkverzameling
  /** De teruglegopmerking, als de inrichter er zelf een heeft getypt (R-75). */
  readonly opmerkingen: readonly Comment[]
  /** Alleen voor een toetser (R-88); zie de kop bij `getWerkverzameling`. */
  readonly gewijzigdeVelden: readonly string[]
  /** Alleen voor een toetser (R-88); zie de kop bij `getWerkverzameling`. */
  readonly vorigeUitkomsten: readonly CriterionOutcome[]
}

/**
 * Wat deze lezer bij dit moment van het voorbeeld moet bekijken, met de teruglegopmerking en
 * het verschil erbij (R-176, R-75, R-77, ontwerp 7.5, 7.6, 8.7).
 *
 * **Dezelfde vier functies als `apps/runtime/src/werkverzameling.ts`'s `getWerkverzameling`,
 * en geen tweede rekenregel.** Het enige verschil is waar de ijkstand vandaan komt: de pod
 * leest hem van de instantie, dit voorbeeld krijgt hem aangeleverd door `Voorbeeld.tsx`, dat
 * hem zelf vastlegt op het moment dat de toetser een opmerking typt. Zonder ijkstand (het
 * paneel is net geopend, of dit formulier heeft geen R-75-veld) is de uitkomst leeg en geen
 * fout: er is dan simpelweg nog niets om op terug te vallen.
 */
export function exampleWerkverzameling(input: ExampleWerkverzamelingInput): ExampleWerkverzameling {
  const event = input.moment === 'begin' ? undefined : input.moment
  const rule = werkverzamelingRule(input.definitie, event)
  const difference = determineDifference(
    sectionsToCompare(input.definitie),
    input.ijkstand?.antwoorden,
    input.stand,
  )
  const outcomes = input.ijkstand?.uitkomsten ?? []

  const werkverzameling = determineWerkverzameling({
    definitie: input.definitie,
    projectie: input.projectie,
    openstaandeCriteria: outstandingCriteria(outcomes),
    verschil: difference,
    regel: rule,
  })

  const visibleFields = new Set(
    input.projectie.secties.flatMap((section) => section.velden.map((field) => field.id)),
  )

  return {
    werkverzameling,
    opmerkingen: commentsFrom(input.definitie, input.projectie, input.ijkstand?.antwoorden),
    // Voor de toets en niet voor de invuller (R-88), en gesorteerd (5.9), net als de pod.
    gewijzigdeVelden:
      input.projectie.rol === 'toetser'
        ? [...difference.velden].filter((fieldId) => visibleFields.has(fieldId)).sort()
        : [],
    vorigeUitkomsten: input.projectie.rol === 'toetser' ? outcomes : [],
  }
}

/**
 * Aan welke kant deze rol staat.
 *
 * De toetser is intern, want een externe gebruiker is nooit toetser (B18). De invuller is
 * extern, want dat is de kant waarvoor het formulier wordt ingericht. Kant en rol staan los
 * (R-138: een NWO-medewerker kan zelf invullen), dus dit is een keuze van het voorbeeld en geen
 * regel van het model. De toekenningen dragen haar, want een toekenning aan de andere kant dan
 * de ingang telt niet; de sectierechten komen er verder niet uit.
 */
function sideOf(role: Role): 'intern' | 'extern' {
  return role === 'toetser' ? 'intern' : 'extern'
}

/** Eén ding dat het voorbeeld heeft aangevuld of weggelaten om te kunnen tonen. */
export interface Adjustment {
  readonly soort: 'aangevuld' | 'weggelaten'
  /** Het pad in de conceptstand, zoals `verhard` het opschrijft. */
  readonly pad: readonly (string | number)[]
  /** Waar het om ging, in de woorden van het gat zelf. */
  readonly wat: string
}

export type ExampleHardening =
  | { readonly ok: true; readonly definitie: Definitie; readonly aanpassingen: readonly Adjustment[] }
  | { readonly ok: false; readonly gaten: readonly Gap[]; readonly aanpassingen: readonly Adjustment[] }

/** Het compartiment dat het voorbeeld invult waar er nog geen is gekozen. */
const EXAMPLE_COMPARTIMENT = 'voorbeeld-compartiment'

/**
 * De conceptstand, klaargemaakt om te tonen.
 *
 * **Waarom dit bestaat.** Het voorbeeld staat naast de instellingen (D-22, ontwerp 8.7) en de
 * renderer krijgt een `Projectie`, dus een strikte `Definitie` (R-99). Een concept waaraan
 * wordt gewerkt is dat vrijwel nooit: wie in het Nederlands begint heeft elke Engelse tekst nog
 * leeg, en `verhard` is met opzet alles-of-niets. Zonder deze stap is het voorbeeld de hele
 * inrichtsessie dood, en dan is D-22 een lege belofte.
 *
 * **Dit is geen tweede uitgang naar een `Definitie`.** De uitkomst gaat naar
 * {@link exampleProjection} en dus naar een `Projectie`; publiceren loopt onveranderd over
 * `verhard` (D-27, D-46). En deze functie beslist zelf niets over wat compleet is: ze leest de
 * gaten die `verhard` meldt en sluit ze, met het pad dat daar al bij staat. Weer één regel, twee
 * lezers.
 *
 * **Twee soorten ingreep, en het verschil is niet willekeurig.**
 *
 * - Wat alleen het publiceren nodig heeft wordt aangevuld: een ontbrekende taal krijgt de melding
 *     zelf te lezen, tussen blokhaken, zodat er in het voorbeeld staat wat er mist en niets wat
 *     voor inhoud kan doorgaan. Een veld zonder compartiment krijgt er een, want een compartiment
 *     is een publiceervraag (D-69) en het voorbeeld rendert een veld met en zonder compartiment
 *     gelijk.
 *   - Wat niet te tonen is wordt weggelaten: een keuzeveld zonder opties, een sectie zonder
 *     velden, een herhaalgroep zonder rijvelden, een criterium zonder schaal, een verwijzing
 *     zonder dossierveld, een regel waarvan het doel weg is. Er wordt niets verzonnen: opties
 *     bijmaken zou de inrichter een keuzelijst laten zien die hij niet heeft gemaakt.
 *
 * Elke ingreep staat in `aanpassingen`, zodat het voorbeeld kan zeggen wat het heeft gedaan.
 * Een voorbeeld dat stilzwijgend de helft weglaat is erger dan geen voorbeeld.
 */
export function exampleDefinitie(draft: DraftDefinitie): ExampleHardening {
  const state = structuredClone(draft) as DraftDefinitie
  const adjustments: Adjustment[] = []

  /**
   * Elke ronde sluit minstens één gat en een gesloten gat komt niet terug, dus de reeks loopt
   * af. Deze grens is er alleen voor het geval dat niet klopt, en dan is de uitkomst de eerlijke
   * stand en geen oneindige lus.
   */
  for (let round = 0; round < 1000; round += 1) {
    const out = harden(state)
    if (out.ok) return { ok: true, definitie: out.definitie, aanpassingen: adjustments }

    const interventions = out.gaten
      .map((gap) => determineIntervention(state, gap))
      .filter((intervention): intervention is Intervention => intervention !== undefined)

    /**
     * Eerst alles wat op zijn plek blijft. Een waarde zetten en een sleutel wissen verschuiven
     * geen index, dus die mogen samen in één ronde. Een element uit een lijst schrappen
     * verschuift wel, en daarvan gaat er per ronde precies één: anders wijst het volgende pad
     * uit dezelfde ronde naast de plank en wordt het verkeerde veld weggelaten.
     */
    let changed = false
    for (const intervention of interventions) {
      if (intervention.actie === 'schrap') continue
      const succeeded =
        intervention.actie === 'zet' ? set(state, intervention.pad, intervention.waarde) : clearKey(state, intervention.pad)
      if (!succeeded) continue
      adjustments.push(adjustmentFor(intervention))
      changed = true
    }

    if (!changed) {
      const toPrune = interventions.find((intervention) => intervention.actie === 'schrap')
      if (toPrune !== undefined && prune(state, toPrune.pad)) {
        adjustments.push(adjustmentFor(toPrune))
        changed = true
      }
    }

    // Een gat waarvoor deze functie geen ingreep kent, zoals een formulier zonder secties.
    if (!changed) return { ok: false, gaten: out.gaten, aanpassingen: adjustments }
  }

  const last = harden(state)
  return last.ok
    ? { ok: true, definitie: last.definitie, aanpassingen: adjustments }
    : { ok: false, gaten: last.gaten, aanpassingen: adjustments }
}

type Path = readonly (string | number)[]

type Intervention =
  | { readonly actie: 'zet'; readonly pad: Path; readonly waarde: string; readonly wat: string }
  | { readonly actie: 'wis'; readonly pad: Path; readonly wat: string }
  | { readonly actie: 'schrap'; readonly pad: Path; readonly wat: string }

function adjustmentFor(intervention: Intervention): Adjustment {
  return {
    soort: intervention.actie === 'zet' ? 'aangevuld' : 'weggelaten',
    pad: intervention.pad,
    wat: intervention.wat,
  }
}

/** De twee taalsleutels van een `TextDraft`; een gat erop is een ontbrekende vertaling. */
const LANGUAGES: ReadonlySet<string> = new Set(['nl', 'en'])

/** Wat dit gat nodig heeft om te kunnen tonen, of niets als deze functie het niet kan sluiten. */
function determineIntervention(state: DraftDefinitie, gap: Gap): Intervention | undefined {
  const last = gap.pad[gap.pad.length - 1]

  if (gap.controle === 'vertaling') {
    // Een ontbrekende taal krijgt de melding zelf te lezen. Een half ingevulde optionele tekst
    // hoort er niet te staan, en die meldt `verhard` op de tekst zelf en niet op een taal.
    return typeof last === 'string' && LANGUAGES.has(last)
      ? { actie: 'zet', pad: gap.pad, waarde: `[${gap.wat}]`, wat: gap.wat }
      : { actie: 'wis', pad: gap.pad, wat: gap.wat }
  }

  if (last === 'compartiment') {
    // Een compartiment is een publiceervraag (D-69) en geen weergavevraag. Er hoort er een en er
    // staat er geen: aanvullen. Er staat er een waar er geen hoort: weglaten.
    return read(state, gap.pad) === undefined
      ? { actie: 'zet', pad: gap.pad, waarde: EXAMPLE_COMPARTIMENT, wat: gap.wat }
      : { actie: 'wis', pad: gap.pad, wat: gap.wat }
  }

  // Een koppeling naar een sectie die het voorbeeld heeft weggelaten (D-05). De sectie zelf
  // weglaten omdat haar koppeling niet meer klopt zou de fout laten uitwaaieren.
  if (last === 'hoortBij') return { actie: 'wis', pad: gap.pad, wat: gap.wat }

  const toPrune = prunablePath(gap.pad)
  if (toPrune === undefined) return undefined
  return { actie: 'schrap', pad: toPrune, wat: gap.wat }
}

/**
 * Het element waarin dit gat zit: de sectie, het veld, het rijveld, de optie of de regel.
 *
 * Het diepste stuk van het pad dat een index is, want dat is het element dat weg kan zonder de
 * rest te raken. Een gat op `['secties']` heeft er geen, en dat is het ene geval waarin er geen
 * voorbeeld is: een formulier zonder secties valt niets van te tonen.
 */
function prunablePath(path: Path): Path | undefined {
  for (let i = path.length - 1; i >= 0; i -= 1) {
    if (typeof path[i] === 'number') return path.slice(0, i + 1)
  }
  return undefined
}

/** De knoop waarin het laatste stuk van dit pad staat, of niets als het pad er niet is. */
function parentOf(
  root: unknown,
  path: Path,
): { ouder: Record<string, unknown> | unknown[]; sleutel: string | number } | undefined {
  const key = path[path.length - 1]
  if (key === undefined) return undefined
  let here: unknown = root
  for (const step of path.slice(0, -1)) {
    if (here === null || typeof here !== 'object') return undefined
    here = (here as Record<string | number, unknown>)[step]
  }
  if (here === null || typeof here !== 'object') return undefined
  return { ouder: here as Record<string, unknown> | unknown[], sleutel: key }
}

function read(root: unknown, path: Path): unknown {
  const place = parentOf(root, path)
  if (place === undefined) return undefined
  return (place.ouder as Record<string | number, unknown>)[place.sleutel]
}

function set(root: unknown, path: Path, value: string): boolean {
  const place = parentOf(root, path)
  if (place === undefined) return false
  ;(place.ouder as Record<string | number, unknown>)[place.sleutel] = value
  return true
}

/** Een sleutel uit een object halen. Op een lijst is dat geen wissen maar een gat erin. */
function clearKey(root: unknown, path: Path): boolean {
  const place = parentOf(root, path)
  if (place === undefined || Array.isArray(place.ouder)) return false
  delete (place.ouder as Record<string, unknown>)[String(place.sleutel)]
  return true
}

/** Een element uit een lijst halen. Dit verschuift de indexen erachter. */
function prune(root: unknown, path: Path): boolean {
  const place = parentOf(root, path)
  if (place === undefined || !Array.isArray(place.ouder) || typeof place.sleutel !== 'number') return false
  if (place.sleutel < 0 || place.sleutel >= place.ouder.length) return false
  place.ouder.splice(place.sleutel, 1)
  return true
}
